import React from 'react';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Pressable,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import {
  formatEnglishDateShort,
  formatEnglishTime,
} from '@/common/utils/formatEnglishDateTime';
import type { OralExamQuestion } from '@/common/api/models/oralExamQuestion';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';
import type { OralExamFillKind } from '@/common/components/oralExamPreviewQuestion';
import type { Task } from '@/student/types';

/** Preview teaser: enough to discover a pool exists, short enough to keep booking in reach. */
const TEASER_ROW_COUNT = 3;
/** Collapsed rows: title + flattened sentence, then ellipsis. */
const COLLAPSED_PREVIEW_LINES = 3;

const SELECTABLE_ROW_SX = { _web: { userSelect: 'text' as const } };

export type OralExamQuestionPoolSurface = 'teaser' | 'full';

interface OralExamQuestionPoolSectionProps {
  task: Task;
  /**
   * ``teaser`` (default, ModuleScreenOld) — first three readable rows, exclusive
   * accordion with chevrons: one open (bullets), others collapsed to 3 lines.
   * ``full`` (TaskScreen) — every question listed, always expanded to bullets, no chevron.
   */
  surface?: OralExamQuestionPoolSurface;
  /** When set, the teaser CTA navigates instead of expanding in place. */
  onOpenAll?: () => void;
  /** Optional retry when the server could not load the pinned question list. */
  onRetry?: () => void;
}

type PoolQuestion = OralExamQuestion & { fillKind?: OralExamFillKind | null };

function hasWebTextSelection(): boolean {
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') {
    return false;
  }
  return (window.getSelection()?.toString().length ?? 0) > 0;
}

/**
 * OralExamQuestionPoolSection
 * ----
 * Student-facing preview of an oral exam's configured question pool.
 * Teaser (module preview): exclusive accordion — collapsed rows are title +
 * flattened sentence (max 3 lines); the open row is title + lead + bullets.
 * Full (TaskScreen): every question always in the bullet view, no chevron.
 */
export const OralExamQuestionPoolSection: React.FC<OralExamQuestionPoolSectionProps> = ({
  task,
  surface = 'teaser',
  onOpenAll,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [openRow, setOpenRow] = React.useState<number | null>(null);
  const [showAll, setShowAll] = React.useState(false);

  const { oralExamConfig } = task;
  if (task.type !== 'oral_exam' || !oralExamConfig) return null;

  const {
    questionPoolLock,
    registeredExamAt,
    questionPoolUnavailable = false,
  } = oralExamConfig;
  const questionPool = (oralExamConfig.questionPool ?? []) as PoolQuestion[];

  if (questionPool.length === 0 && !questionPoolUnavailable) return null;

  const formatDate = (iso: string) => formatEnglishDateShort(iso);
  const formatTime = (iso: string) => formatEnglishTime(iso);

  const lockNote = (() => {
    if (questionPoolUnavailable) return null;
    if (questionPoolLock === 'open') {
      return t('student.module.oralExamQuestionPool.lockOpen');
    }
    if (questionPoolLock === 'registered') {
      if (registeredExamAt) {
        return t('student.module.oralExamQuestionPool.lockRegistered', {
          date: formatDate(registeredExamAt),
          time: formatTime(registeredExamAt),
        });
      }
      return t('student.module.oralExamQuestionPool.lockRegisteredNoDate');
    }
    return null;
  })();

  const isTeaser = surface === 'teaser';
  const toggleRow = (index: number) => {
    if (!isTeaser || hasWebTextSelection()) return;
    setOpenRow((current) => (current === index ? null : index));
  };

  const isTruncated = isTeaser && !showAll && questionPool.length > TEASER_ROW_COUNT;
  const visibleQuestions = isTruncated
    ? questionPool.slice(0, TEASER_ROW_COUNT)
    : questionPool;
  const showOpenAllCta = isTeaser && Boolean(onOpenAll) && questionPool.length > TEASER_ROW_COUNT;
  const showInPlaceToggle = isTeaser && !onOpenAll && questionPool.length > TEASER_ROW_COUNT;

  return (
    <VStack space="sm" testID="oral-exam-question-pool-section">
      <Heading size="sm">
        {t('student.module.oralExamQuestionPool.titleWithCount', { count: questionPool.length })}
      </Heading>

      {lockNote ? (
        <HStack
          space="xs"
          alignItems="flex-start"
          testID="oral-exam-pool-lock-note"
        >
          <Icon
            as={Info}
            size="xs"
            color="$textHint"
            flexShrink={0}
            mt="$0.5"
            testID="oral-exam-pool-lock-note-icon"
          />
          <Text size="xs" color="$textHint" flex={1}>
            {lockNote}
          </Text>
        </HStack>
      ) : null}

      {questionPoolUnavailable ? (
        <VStack space="xs" testID="oral-exam-pool-load-error">
          <Text size="sm" color="$textBody" fontWeight="medium">
            {t('student.module.oralExamQuestionPool.poolLoadErrorTitle')}
          </Text>
          <Text size="xs" color="$textHint">
            {t('student.module.oralExamQuestionPool.poolLoadErrorBody')}
          </Text>
          <Pressable
            onPress={onRetry}
            alignSelf="flex-start"
            hitSlop={8}
            testID="oral-exam-pool-retry"
          >
            <Text size="xs" color="$primary600" fontWeight="medium">
              {t('student.module.oralExamQuestionPool.tryAgain')}
            </Text>
          </Pressable>
        </VStack>
      ) : (
        <>
          <VStack borderWidth={1} borderColor="$borderLight" borderRadius="$md" overflow="hidden">
            {visibleQuestions.map((question, index) => {
              const isOpen = !isTeaser || openRow === index;
              const rowBody = (
                <HStack space="sm" alignItems="flex-start">
                  <Text
                    size="2xs"
                    color="$textHint"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="$md"
                    mt="$0.5"
                    minWidth={20}
                    selectable
                    sx={SELECTABLE_ROW_SX}
                    testID={`oral-exam-pool-question-heading-${index}`}
                  >
                    {index + 1}
                  </Text>
                  <Box flex={1} sx={SELECTABLE_ROW_SX}>
                    <OralExamQuestionText
                      question={question.question ?? ''}
                      size="xs"
                      numberOfLines={isOpen ? undefined : COLLAPSED_PREVIEW_LINES}
                      titleColor="$textHeading"
                      textColor="$textBody"
                      showFillSlot
                      fillKind={question.fillKind}
                      fillKindBadgeTestID={
                        isOpen
                          ? `oral-exam-fill-kind-badge-${index}`
                          : `oral-exam-fill-kind-preview-${index}`
                      }
                      testID={
                        isOpen
                          ? undefined
                          : `oral-exam-pool-question-preview-${index}`
                      }
                    />
                  </Box>
                  {isTeaser ? (
                    <Icon
                      as={isOpen ? ChevronUp : ChevronDown}
                      size="xs"
                      color="$textHint"
                      mt="$0.5"
                      testID={`oral-exam-pool-question-chevron-${index}`}
                    />
                  ) : null}
                </HStack>
              );
              const rowKey = question.questionId ?? `oral-exam-question-${index}`;
              const rowChrome = {
                bg: isTeaser && isOpen ? '$bgSubtle' as const : undefined,
                borderTopWidth: index === 0 ? 0 : 1,
                borderTopColor: '$borderLight' as const,
                px: '$3' as const,
                py: '$2' as const,
                sx: SELECTABLE_ROW_SX,
                testID: `oral-exam-pool-question-row-${index}`,
              };
              if (!isTeaser) {
                return (
                  <Box key={rowKey} {...rowChrome}>
                    {rowBody}
                  </Box>
                );
              }
              return (
                <Pressable
                  key={rowKey}
                  onPress={() => toggleRow(index)}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: isOpen }}
                  {...rowChrome}
                >
                  {rowBody}
                </Pressable>
              );
            })}
          </VStack>

          {showOpenAllCta && (
            <Pressable
              onPress={onOpenAll}
              alignSelf="flex-start"
              flexDirection="row"
              alignItems="center"
              hitSlop={8}
              testID="oral-exam-question-pool-open-all"
            >
              <Text size="xs" color="$primary600" fontWeight="medium">
                {t('student.module.oralExamQuestionPool.openAll', { count: questionPool.length })}
              </Text>
              <Icon as={ArrowRight} size="xs" color="$primary600" ml="$1" />
            </Pressable>
          )}

          {showInPlaceToggle && (
            <Pressable
              onPress={() => setShowAll((current) => !current)}
              alignSelf="flex-start"
              flexDirection="row"
              alignItems="center"
              hitSlop={8}
              testID="oral-exam-question-pool-show-all"
            >
              <Text size="xs" color="$primary600" fontWeight="medium">
                {isTruncated
                  ? t('student.module.oralExamQuestionPool.showAll', { count: questionPool.length })
                  : t('student.module.oralExamQuestionPool.showFewer')}
              </Text>
              <Icon as={isTruncated ? ChevronDown : ChevronUp} size="xs" color="$primary600" ml="$1" />
            </Pressable>
          )}
        </>
      )}
    </VStack>
  );
};

export default OralExamQuestionPoolSection;
